import type {
  Effort,
  EffortOption,
  FamilyOption,
  LabOption,
  ModelOption,
  ModelOptions,
  PresetReasoning,
  TurnOptions,
} from "@esi/shared";
import { effectivePresetLevel, reasoningLevelsOf } from "@esi/shared";
import { Brain, ChevronDown, ChevronRight, Gauge, Info, Lock, Pin, Zap } from "lucide-react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * The composer's effort picker (spec `model-choice`, model-picking D9): Fast, Balanced or Deep
 * within what the scope allows, each with the family and version it resolves to and what it
 * implies before it is picked; when family choice is on, the other families of the scope's labs
 * (grouped by lab when it offers more than one), never a list of versions; and, for a lab with
 * more than one provider, the provider this conversation goes through; and, under Advanced, a
 * reasoning level for this conversation where the version reasons (D15, Q19). The choice is a
 * preference: the server re-resolves it every turn, so this only shows what the policy offers
 * and sends what the user picked.
 */

/** What the user picked here; null fields mean "not picked" (the stored or default applies). */
export interface PickerState {
  effort: Effort | null;
  /** A family ID (`anthropic/claude-opus`), never a version. */
  family: string | null;
  /** Lab → provider picked for this conversation. */
  labProviders?: Record<string, string> | undefined;
  /**
   * The conversation's reasoning level (Q19): a level, null for the preset's (sent as
   * `preset` to clear a stored one), undefined when never set here or stored.
   */
  reasoning?: PresetReasoning | null | undefined;
}

const EFFORT_ICONS = { fast: Zap, balanced: Gauge, deep: Brain } as const;

/** The picker state of a composer: what the user picked, else the conversation's stored choice. */
export function useEffortChoice(options: ModelOptions | undefined) {
  const [picked, setPicked] = useState<PickerState | null>(null);
  const current: PickerState = picked ?? {
    effort: options?.choice?.effort ?? null,
    // The stored family (a stored exact key comes back read as its family).
    family: options?.choice?.model ?? null,
    labProviders: options?.choice?.labProviders,
    reasoning: options?.choice?.reasoning ?? undefined,
  };
  const view = options ? pickerView(options, current) : null;
  return {
    view,
    current,
    pick: setPicked,
    /** What a message carries (`sendMessageRequestSchema.options`); none until options load. */
    turnOptions: (): TurnOptions | undefined =>
      view
        ? {
            // Only what was chosen: an untouched picker leaves the scope's default to apply,
            // so a later default or policy change reaches the conversation.
            ...(current.effort !== null && { effort: current.effort }),
            // The pick as the user made it, even when refused now (design D3): the server
            // resolves it and keeps it for when the policy allows it again.
            ...(current.family !== null && { family: current.family }),
            // Only the picks the options still offer: a withdrawn one is never resent.
            ...(Object.keys(view.pickedProviders).length > 0 && {
              labProviders: view.pickedProviders,
            }),
            // Like the effort, as the user set it; the server ignores it when locked or when the
            // version does not reason, and keeps it.
            ...(current.reasoning !== undefined && { reasoning: current.reasoning ?? "preset" }),
          }
        : undefined,
  };
}

export interface PickerView {
  options: ModelOptions;
  /** The effort the next turn uses. */
  effort: Effort;
  /** The honored family pick (another family than the effort's), if any. */
  family: FamilyOption | null;
  /** The other families the effort may run on (family choice on). */
  families: FamilyOption[];
  /** The labs whose provider users may pick here (more than one choice, not locked). */
  providerLabs: LabOption[];
  /** Lab → the provider the next turn uses for it. */
  providers: Record<string, string>;
  /** Lab → the provider the user picked (only explicit picks, sent with a message). */
  pickedProviders: Record<string, string>;
  /** The picked effort or family the policy no longer allows (shown, not honored). */
  unavailable: { effort: Effort | null; family: string | null } | null;
  /** The conversation's reasoning level as set; null: cleared, undefined: never set. */
  reasoning: PresetReasoning | null | undefined;
}

/**
 * The reasoning level an effort's turn runs at (Q19), as the server resolves it: the
 * conversation's level when it may apply (not locked, the version reasons), else the preset's,
 * lowered to what the version supports.
 */
export function effortReasoning(
  option: EffortOption,
  conversation: PresetReasoning | null | undefined,
): PresetReasoning {
  const r = option.reasoning;
  if (!r) {
    return "default";
  }
  const own = conversation != null && !r.locked && r.levels.length > 1 ? conversation : r.level;
  return effectivePresetLevel(own, r.levels);
}

/** What the next turn runs on, as the server will resolve it (D9). */
export function pickerView(options: ModelOptions, current: PickerState): PickerView {
  // No stored effort: the default, as the server resolves it.
  const allowed = options.efforts.find(
    (e) => e.effort === (current.effort ?? options.defaultEffort),
  );
  const effort = allowed ? allowed.effort : options.defaultEffort;
  const own = options.efforts.find((e) => e.effort === effort)?.family?.id;
  const families = options.advanced
    ? (options.families ?? []).filter((f) => f.efforts.includes(effort) && f.id !== own)
    : [];
  const family =
    allowed && current.family !== null && current.family !== own
      ? (families.find((f) => f.id === current.family) ?? null)
      : null;
  const effortRefused = current.effort !== null && !allowed;
  const familyRefused = current.family !== null && current.family !== own && family === null;
  const labs = options.labs ?? [];
  const providers = Object.fromEntries(
    labs.map((lab) => {
      const pick = current.labProviders?.[lab.lab];
      const ok = pick !== undefined && !lab.locked && lab.providers.some((p) => p.id === pick);
      return [lab.lab, ok ? pick : lab.defaultProvider];
    }),
  );
  return {
    options,
    effort,
    family,
    families,
    // Only picks still offered: a withdrawn one is not sent again (the server drops it too).
    pickedProviders: Object.fromEntries(
      Object.entries(current.labProviders ?? {}).filter(([lab, provider]) =>
        labs.some((l) => l.lab === lab && !l.locked && l.providers.some((p) => p.id === provider)),
      ),
    ),
    providerLabs: labs.filter((l) => l.providers.length > 1 && !l.locked),
    providers,
    reasoning: current.reasoning,
    unavailable:
      effortRefused || familyRefused
        ? {
            effort: effortRefused ? current.effort : null,
            family: familyRefused ? current.family : null,
          }
        : null,
  };
}

/**
 * The Advanced reasoning control of an effort (Q19): the levels of the version the turn runs on
 * (the picked family's, else the effort's own), as the server checks them; null when that
 * version does not reason or the levels are locked for the scope.
 */
export function reasoningControlOf(
  option: EffortOption,
  family: FamilyOption | null,
): { level: PresetReasoning; levels: PresetReasoning[] } | null {
  const r = option.reasoning;
  if (!r || r.locked) {
    return null;
  }
  const levels = family ? reasoningLevelsOf(family.model.key) : r.levels;
  // The preset as set (older servers: as lowered for the effort's own version).
  const preset = r.preset ?? r.level;
  return levels.length > 1 ? { level: effectivePresetLevel(preset, levels), levels } : null;
}

/** A provider's name: ESI's own, the tenant's own Bedrock, else the server's display name. */
function useProviderName() {
  const { t } = useTranslation();
  return (provider: { id: string; displayName: string }) =>
    provider.id === "esi"
      ? t("models.picker.providers.esi")
      : provider.id === "tenant:bedrock"
        ? t("models.picker.providers.tenantBedrock")
        : provider.displayName;
}

export function EffortPicker(props: {
  view: PickerView;
  onPick: (state: PickerState) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { view, onPick } = props;
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [familiesOpen, setFamiliesOpen] = useState(view.family !== null);
  const [advancedOpen, setAdvancedOpen] = useState(view.reasoning != null);
  const providerName = useProviderName();
  const { options } = view;
  const Icon = options.locked ? Lock : EFFORT_ICONS[view.effort];
  const name = t(`models.picker.efforts.${view.effort}.name`);
  const selected = options.efforts.find((e) => e.effort === view.effort);
  const state = (over: Partial<PickerState>): PickerState => ({
    effort: view.effort,
    family: view.family?.id ?? null,
    labProviders: view.pickedProviders,
    ...(view.reasoning !== undefined && { reasoning: view.reasoning }),
    ...over,
  });
  // The Advanced reasoning control: only where the version reasons and the levels are not
  // locked for the scope (Q19).
  const reasoningControl = selected ? reasoningControlOf(selected, view.family) : null;
  // Grouped by lab only when the families come from more than one lab.
  const groups = groupByLab(view.families);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={props.disabled}
          aria-label={
            options.locked
              ? options.lockedBy
                ? t("models.picker.triggerLocked", {
                    effort: name,
                    level: t(`models.picker.levels.${options.lockedBy}`),
                  })
                : t("models.picker.triggerOnly", { effort: name })
              : view.family
                ? t("models.picker.triggerFamily", {
                    effort: name,
                    family: view.family.displayName,
                  })
                : t("models.picker.trigger", { effort: name })
          }
          className={cn(
            "inline-flex h-8 min-w-0 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[13px] text-fg-secondary outline-none transition-colors duration-(--duration-fast) hover:bg-surface-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-surface-hover data-[state=open]:text-fg",
            props.className,
          )}
        >
          <Icon aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" strokeWidth={1.75} />
          <span className="truncate">{name}</span>
          {view.family ? (
            <span className="hidden max-w-[10rem] truncate text-fg-muted sm:inline">
              · {view.family.displayName}
            </span>
          ) : null}
          {view.unavailable || (selected?.pinned && !selected.pinHonoured) ? (
            <Info
              aria-hidden="true"
              className="size-3.5 shrink-0 text-fg-muted"
              strokeWidth={1.75}
            />
          ) : null}
          <ChevronDown
            aria-hidden="true"
            className="size-3.5 shrink-0 text-fg-muted"
            strokeWidth={1.75}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        collisionPadding={12}
        className="max-h-[min(36rem,calc(100dvh-6rem))] w-[min(22rem,calc(100vw-2rem))] gap-0 overflow-y-auto rounded-lg border-line bg-surface-raised p-1.5 text-fg shadow-overlay"
        aria-labelledby={`${id}-title`}
      >
        <div className="flex items-baseline justify-between gap-3 px-2.5 pt-1.5 pb-2">
          <h2 id={`${id}-title`} className="text-[13px] font-medium text-fg">
            {t("models.picker.title")}
          </h2>
          {options.locked ? (
            <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
              <Lock aria-hidden="true" className="size-3" strokeWidth={1.75} />
              {options.lockedBy
                ? t("models.picker.lockedBy", {
                    level: t(`models.picker.levels.${options.lockedBy}`),
                  })
                : t("models.picker.onlyEffort")}
            </span>
          ) : null}
        </div>
        {view.unavailable ? (
          <UnavailableLine
            view={view}
            onAccept={() => onPick(state({ effort: view.effort, family: null }))}
          />
        ) : null}
        {selected?.pinned && !selected.pinHonoured && !view.family ? (
          <InfoLine>
            {t("models.picker.pinUnhonoured", {
              effort: name,
              model: selected.model.displayName,
            })}
          </InfoLine>
        ) : null}
        <RadioGroupPrimitive.Root
          value={view.effort}
          aria-labelledby={`${id}-title`}
          className="grid gap-0.5"
          onValueChange={(value) => {
            const effort = value as Effort;
            // A family pick follows the effort when that effort may run on it too.
            const keep =
              view.family &&
              options.families?.some((f) => f.id === view.family?.id && f.efforts.includes(effort));
            onPick(state({ effort, family: keep ? (view.family?.id ?? null) : null }));
          }}
        >
          {options.efforts.map((option) => (
            <EffortRow
              key={option.effort}
              option={option}
              disabled={options.locked}
              level={effortReasoning(option, view.reasoning)}
            />
          ))}
        </RadioGroupPrimitive.Root>
        {view.families.length > 0 && selected ? (
          <div className="mt-1.5 border-t border-line pt-1.5">
            <button
              type="button"
              aria-expanded={familiesOpen}
              aria-controls={`${id}-families`}
              onClick={() => setFamiliesOpen((o) => !o)}
              className="flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-[13px] font-medium text-fg-secondary outline-none transition-colors duration-(--duration-fast) hover:bg-surface-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight
                aria-hidden="true"
                className={cn(
                  "size-3.5 text-fg-muted transition-transform duration-(--duration-fast)",
                  familiesOpen && "rotate-90",
                )}
                strokeWidth={1.75}
              />
              {t("models.picker.otherFamilies", { effort: name })}
            </button>
            {familiesOpen ? (
              <RadioGroupPrimitive.Root
                id={`${id}-families`}
                value={view.family?.id ?? ""}
                aria-label={t("models.picker.otherFamilies", { effort: name })}
                className="grid gap-0.5 pt-0.5"
                onValueChange={(value) => onPick(state({ family: value === "" ? null : value }))}
              >
                <FamilyRow
                  value=""
                  name={selected.family?.displayName ?? t("models.picker.automatic")}
                  hint={t("models.picker.effortFamily", { effort: name })}
                  model={selected.model}
                />
                {groups.map((group) => (
                  <div key={group.lab} role="presentation" className="grid gap-0.5">
                    {groups.length > 1 ? (
                      <p
                        aria-hidden="true"
                        className="px-2.5 pt-2 pb-0.5 text-xs font-medium text-fg-muted"
                      >
                        {group.labName}
                      </p>
                    ) : null}
                    {group.families.map((f) => (
                      <FamilyRow
                        key={f.id}
                        value={f.id}
                        name={f.displayName}
                        {...(groups.length > 1 && { label: `${f.displayName}, ${f.labName}` })}
                        model={f.model}
                      />
                    ))}
                  </div>
                ))}
              </RadioGroupPrimitive.Root>
            ) : null}
          </div>
        ) : null}
        {reasoningControl ? (
          <div className="mt-1.5 border-t border-line pt-1.5">
            <button
              type="button"
              aria-expanded={advancedOpen}
              aria-controls={`${id}-advanced`}
              onClick={() => setAdvancedOpen((o) => !o)}
              className="flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-[13px] font-medium text-fg-secondary outline-none transition-colors duration-(--duration-fast) hover:bg-surface-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight
                aria-hidden="true"
                className={cn(
                  "size-3.5 text-fg-muted transition-transform duration-(--duration-fast)",
                  advancedOpen && "rotate-90",
                )}
                strokeWidth={1.75}
              />
              {t("models.picker.advanced")}
            </button>
            {advancedOpen ? (
              <div id={`${id}-advanced`} className="px-2.5 pt-1 pb-1.5">
                <p id={`${id}-reasoning`} className="text-xs font-medium text-fg-muted">
                  {t("models.picker.reasoning.title")}
                </p>
                <RadioGroupPrimitive.Root
                  value={view.reasoning ?? "preset"}
                  aria-labelledby={`${id}-reasoning`}
                  className="mt-1.5 grid grid-cols-2 gap-0.5 rounded-md bg-surface-sunken p-0.5"
                  onValueChange={(value) =>
                    onPick(
                      state({ reasoning: value === "preset" ? null : (value as PresetReasoning) }),
                    )
                  }
                >
                  {(["preset", ...reasoningControl.levels] as const).map((level) => (
                    <RadioGroupPrimitive.Item
                      key={level}
                      value={level}
                      className={cn(
                        "h-7 min-w-0 truncate rounded-[5px] px-2 text-[13px] text-fg-secondary outline-none transition-colors duration-(--duration-fast) hover:text-fg focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-surface-raised data-[state=checked]:font-medium data-[state=checked]:text-fg data-[state=checked]:ring-1 data-[state=checked]:ring-line",
                        level === "preset" && "col-span-2",
                      )}
                    >
                      {level === "preset"
                        ? t("models.picker.reasoning.preset", {
                            effort: name,
                            level: t(`models.picker.reasoning.levels.${reasoningControl.level}`),
                          })
                        : t(`models.picker.reasoning.levels.${level}`)}
                    </RadioGroupPrimitive.Item>
                  ))}
                </RadioGroupPrimitive.Root>
                <p className="mt-1.5 text-xs text-fg-muted">{t("models.picker.reasoning.hint")}</p>
              </div>
            ) : null}
          </div>
        ) : null}
        {view.providerLabs.map((lab) => (
          <div key={lab.lab} className="mt-1.5 border-t border-line px-2.5 pt-2 pb-1.5">
            <p id={`${id}-through-${lab.lab}`} className="text-xs font-medium text-fg-muted">
              {view.providerLabs.length > 1 || (options.labs?.length ?? 0) > 1
                ? t("models.picker.throughLab", { lab: lab.displayName })
                : t("models.picker.through")}
            </p>
            <RadioGroupPrimitive.Root
              value={view.providers[lab.lab] ?? lab.defaultProvider}
              aria-labelledby={`${id}-through-${lab.lab}`}
              className="mt-1.5 flex w-full gap-0.5 rounded-md bg-surface-sunken p-0.5"
              onValueChange={(value) =>
                onPick(state({ labProviders: { ...view.pickedProviders, [lab.lab]: value } }))
              }
            >
              {lab.providers.map((p) => (
                <RadioGroupPrimitive.Item
                  key={p.id}
                  value={p.id}
                  className="h-7 min-w-0 flex-1 truncate rounded-[5px] px-2 text-[13px] text-fg-secondary outline-none transition-colors duration-(--duration-fast) hover:text-fg focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-surface-raised data-[state=checked]:font-medium data-[state=checked]:text-fg data-[state=checked]:ring-1 data-[state=checked]:ring-line"
                >
                  {providerName(p)}
                </RadioGroupPrimitive.Item>
              ))}
            </RadioGroupPrimitive.Root>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** The families in lab order, each lab once (the server lists the primary lab first). */
function groupByLab(families: readonly FamilyOption[]) {
  const groups: { lab: string; labName: string; families: FamilyOption[] }[] = [];
  for (const f of families) {
    const group = groups.find((g) => g.lab === f.lab);
    if (group) {
      group.families.push(f);
    } else {
      groups.push({ lab: f.lab, labName: f.labName, families: [f] });
    }
  }
  return groups;
}

function InfoLine({ children }: { children: React.ReactNode }) {
  return (
    <p className="mx-1 mb-1.5 flex items-start gap-2 rounded-md bg-surface-sunken px-2.5 py-2 text-xs text-fg-secondary">
      <Info
        aria-hidden="true"
        className="mt-px size-3.5 shrink-0 text-fg-muted"
        strokeWidth={1.75}
      />
      <span className="min-w-0 flex-1">{children}</span>
    </p>
  );
}

/** The refused choice, said plainly (never which setting refused it). */
function UnavailableLine({ view, onAccept }: { view: PickerView; onAccept: () => void }) {
  const { t } = useTranslation();
  const fallback = t(`models.picker.efforts.${view.effort}.name`);
  const text = view.unavailable?.effort
    ? t("models.picker.unavailableEffort", {
        effort: t(`models.picker.efforts.${view.unavailable.effort}.name`),
        fallback,
      })
    : t("models.picker.unavailableFamily", { effort: fallback });
  return (
    <p className="mx-1 mb-1.5 flex items-start gap-2 rounded-md bg-surface-sunken px-2.5 py-2 text-xs text-fg-secondary">
      <Info
        aria-hidden="true"
        className="mt-px size-3.5 shrink-0 text-fg-muted"
        strokeWidth={1.75}
      />
      <span className="min-w-0 flex-1">
        {text}{" "}
        {/* Replaces the refused pick with what answers now; the radio cannot, it is checked. */}
        <button
          type="button"
          onClick={onAccept}
          className="rounded-sm font-medium text-fg underline underline-offset-2 outline-none hover:text-fg-secondary focus-visible:ring-2 focus-visible:ring-ring"
        >
          {view.unavailable?.effort
            ? t("models.picker.useFallback", { effort: fallback })
            : t("models.picker.clearFamily")}
        </button>
      </span>
    </p>
  );
}

/** Row metrics shared by efforts and models: the whole row is the radio. */
const ROW =
  "group grid w-full grid-cols-[1rem_1fr] gap-x-2.5 rounded-md px-2.5 py-2 text-left outline-none transition-colors duration-(--duration-fast) hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-surface-selected disabled:cursor-default disabled:hover:bg-transparent disabled:data-[state=checked]:bg-surface-selected";

function EffortRow({
  option,
  disabled,
  level,
}: {
  option: EffortOption;
  disabled: boolean;
  /** The reasoning level its turn runs at; shown beside the version unless Model default. */
  level: PresetReasoning;
}) {
  const { t } = useTranslation();
  const Icon = EFFORT_ICONS[option.effort];
  return (
    <RadioGroupPrimitive.Item value={option.effort} disabled={disabled} className={ROW}>
      <Icon
        aria-hidden="true"
        className="mt-0.5 size-4 text-fg-muted group-data-[state=checked]:text-fg"
        strokeWidth={1.75}
      />
      <span className="grid min-w-0 gap-0.5">
        <span className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-fg">
            {t(`models.picker.efforts.${option.effort}.name`)}
          </span>
          <span className="inline-flex min-w-0 items-center gap-1 text-xs text-fg-muted group-data-[state=checked]:text-fg-secondary">
            {option.pinned && option.pinHonoured ? (
              <>
                <Pin aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
                <span className="sr-only">{t("models.picker.pinned")} </span>
              </>
            ) : null}
            <span className="truncate">
              {level === "default"
                ? option.model.displayName
                : t("models.picker.reasoning.line", {
                    model: option.model.displayName,
                    level: t(`models.picker.reasoning.lineLevels.${level}`),
                  })}
            </span>
          </span>
        </span>
        <span className="text-[13px] text-fg-secondary">
          {t(`models.picker.efforts.${option.effort}.hint`)}
        </span>
        <ImplicationLine model={option.model} />
      </span>
    </RadioGroupPrimitive.Item>
  );
}

function FamilyRow(props: {
  value: string;
  name: string;
  /** The accessible name when the visible one needs its lab. */
  label?: string;
  hint?: string;
  model: ModelOption;
}) {
  return (
    <RadioGroupPrimitive.Item
      value={props.value}
      {...(props.label && { "aria-label": props.label })}
      className={ROW}
    >
      <span
        aria-hidden="true"
        className="mt-1 grid size-3.5 place-items-center rounded-full border border-line-strong group-data-[state=checked]:border-fg"
      >
        <span className="hidden size-1.5 rounded-full bg-fg group-data-[state=checked]:block" />
      </span>
      <span className="grid min-w-0 gap-0.5">
        <span className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-medium text-fg">{props.name}</span>
          <span className="truncate text-xs text-fg-muted group-data-[state=checked]:text-fg-secondary">
            {props.model.displayName}
          </span>
        </span>
        {props.hint ? (
          <span className="text-xs text-fg-muted group-data-[state=checked]:text-fg-secondary">
            {props.hint}
          </span>
        ) : null}
        <ImplicationLine model={props.model} />
      </span>
    </RadioGroupPrimitive.Item>
  );
}

const COST_KEYS = [
  "models.picker.cost.lowest",
  "models.picker.cost.moderate",
  "models.picker.cost.higher",
  "models.picker.cost.highest",
] as const;
const SPEED_KEYS = [
  "models.picker.speed.slower",
  "models.picker.speed.moderate",
  "models.picker.speed.fastest",
] as const;

/**
 * What using a model implies here, in one quiet line: retention, where it is processed, the
 * provider's access, and relative cost and speed (spec `model-choice` "Implications at the
 * point of choice").
 */
export function ImplicationLine({ model }: { model: ModelOption }) {
  const { t } = useTranslation();
  const i = model.implications;
  const parts = [
    t(`models.picker.retention.${i.retention}`),
    t(`models.picker.processed.${model.geography}`),
    t(i.providerReceivesData ? "models.picker.providerReceives" : "models.picker.providerNever"),
    t(COST_KEYS[i.relativeCost - 1] ?? "models.picker.cost.moderate"),
    t(SPEED_KEYS[i.relativeSpeed - 1] ?? "models.picker.speed.moderate"),
  ];
  return (
    <span className="text-xs leading-relaxed text-fg-muted group-data-[state=checked]:text-fg-secondary">
      {parts.map((part, n) => (
        <span key={part} className="inline">
          {n > 0 ? <span aria-hidden="true"> · </span> : null}
          {n > 0 ? <span className="sr-only">, </span> : null}
          {part}
        </span>
      ))}
    </span>
  );
}
